import React, { useEffect, useState } from "react";
import { Input, Select } from "antd";

import type { EmailTemplateCategory } from "../../../../core/data/redux/email-templates/types";

interface FiltersValue {
  search: string;
  category?: string;
  status: "all" | "active" | "inactive";
}

interface Props {
  value: FiltersValue;
  categories: EmailTemplateCategory[];
  onChange: (partial: Partial<FiltersValue>) => void;
  onReset: () => void;
  totalCount: number;
  activeCount: number;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const filterOption = (input: string, option?: { label: string; value: any }) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const EmailTemplatesFilters: React.FC<Props> = ({
  value,
  categories,
  onChange,
  onReset,
  totalCount,
  activeCount,
}) => {
  const [localSearch, setLocalSearch] = useState(value.search || "");

  useEffect(() => {
    setLocalSearch(value.search || "");
  }, [value.search]);

  const activeFiltersCount =
    (value.search ? 1 : 0) +
    (value.category ? 1 : 0) +
    (value.status !== "all" ? 1 : 0);

  const handleSearchCommit = () => {
    onChange({ search: localSearch.trim() });
  };

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i
                className="ti ti-mail-forward text-info me-2"
                style={{ fontSize: 20 }}
              />
              Email Templates
            </h4>
            <p className="text-muted mb-0">
              Manage transactional emails sent across all modules — use{" "}
              <code>{`{{variable}}`}</code> placeholders.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div
              className="badge bg-light text-dark"
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <i className="ti ti-mail me-1 text-primary" />
              {totalCount} Templates
            </div>
            <div
              className="badge bg-light text-success"
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #dcfce7",
              }}
            >
              <i className="ti ti-check me-1" />
              {activeCount} Active
            </div>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
        <div className="d-flex gap-2 flex-wrap">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search by name, code, or subject…"
              value={localSearch}
              onChange={(e) => {
                const v = e.target.value;
                setLocalSearch(v);
                if (v === "") {
                  onChange({ search: "" });
                }
              }}
              onPressEnter={handleSearchCommit}
              style={{ width: 280, minHeight: 38 }}
              allowClear
            />
            <button
              type="button"
              className="btn btn-info"
              onClick={handleSearchCommit}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-search" />
            </button>
          </div>
        </div>

        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-2">
          <div className="me-2 mb-2">
            <Select
              showSearch
              allowClear
              filterOption={filterOption}
              style={{ width: 200, minHeight: 38 }}
              placeholder="Category"
              value={value.category || undefined}
              onChange={(v) => onChange({ category: v || undefined })}
              options={categories
                .filter((c) => c.is_active)
                .map((c) => ({
                  value: c.value,
                  label: c.icon ? `${c.icon}  ${c.label}` : c.label,
                }))}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              style={{ width: 160, minHeight: 38 }}
              placeholder="Status"
              value={value.status}
              onChange={(v) =>
                onChange({ status: (v as FiltersValue["status"]) || "all" })
              }
              options={STATUS_OPTIONS}
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${
                activeFiltersCount > 0
                  ? "btn-warning"
                  : "btn-outline-danger"
              } mb-2 position-relative py-2`}
              onClick={onReset}
              disabled={activeFiltersCount === 0}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: 10 }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesFilters;
